/**
 * Cloud Backup Service
 * Handles backup/restore to Google Drive and Dropbox
 */

import { exportAllData, importAllData } from './storage';

// ============== GOOGLE DRIVE ==============

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GOOGLE_SCOPES = 'https://www.googleapis.com/auth/drive.file';
const BACKUP_FILENAME = 'minddeck_backup.json';

let googleTokenClient = null;
let googleAccessToken = null;

/**
 * Initialize Google Drive API
 */
export function initGoogleDrive() {
  return new Promise((resolve) => {
    if (!GOOGLE_CLIENT_ID) {
      console.warn('Google Drive integration not configured - no client ID');
      resolve(false);
      return;
    }

    // Try to restore token from sessionStorage first
    try {
      const storedToken = sessionStorage.getItem('google_access_token');
      if (storedToken) {
        googleAccessToken = storedToken;
        console.log('Restored Google token from sessionStorage');
      }
    } catch (e) {
      console.warn('Could not read token from sessionStorage:', e);
    }

    // Check if already loaded
    if (typeof google !== 'undefined' && google.accounts) {
      console.log('Google Identity Services already loaded');
      initTokenClient();
      resolve(true);
      return;
    }

    // Load Google Identity Services
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('Google Identity Services loaded');
      initTokenClient();
      resolve(true);
    };
    script.onerror = (err) => {
      console.error('Failed to load Google Identity Services:', err);
      resolve(false);
    };
    document.body.appendChild(script);
  });
}

function initTokenClient() {
  try {
    console.log('Initializing Google token client with client_id:', GOOGLE_CLIENT_ID);
    // eslint-disable-next-line no-undef
    googleTokenClient = google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: GOOGLE_SCOPES,
      callback: (response) => {
        console.log('=== INIT Token callback fired ===');
        console.log('Response:', JSON.stringify(response, null, 2));
        if (response.access_token) {
          googleAccessToken = response.access_token;
          try {
            sessionStorage.setItem('google_access_token', response.access_token);
            console.log('Token saved to sessionStorage');
          } catch (e) {
            console.error('Failed to save token:', e);
          }
        }
      },
      error_callback: (error) => {
        console.error('=== INIT Error callback fired ===');
        console.error('Error object:', JSON.stringify(error, null, 2));
      }
    });
    console.log('Token client initialized successfully:', googleTokenClient);
  } catch (err) {
    console.error('Error initializing token client:', err);
  }
}

/**
 * Check if running on mobile
 */
export function isMobileDevice() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * Start OAuth redirect flow (for mobile)
 * This redirects the user to Google, then back to the app with a token
 */
export function startGoogleRedirectAuth() {
  console.log('=== startGoogleRedirectAuth called ===');
  console.log('GOOGLE_CLIENT_ID:', GOOGLE_CLIENT_ID);
  console.log('window.location.origin:', window.location.origin);
  
  if (!GOOGLE_CLIENT_ID) {
    console.error('No Google Client ID configured!');
    alert('Google Drive is not configured. Missing client ID.');
    return;
  }
  
  const redirectUri = window.location.origin + '/sync';
  const state = Math.random().toString(36).substring(7);
  
  console.log('redirectUri:', redirectUri);
  console.log('state:', state);
  
  // Store state for verification when user returns
  sessionStorage.setItem('google_oauth_state', state);
  sessionStorage.setItem('google_oauth_pending', 'true');
  
  // Build OAuth URL for implicit grant flow
  const authUrl = 'https://accounts.google.com/o/oauth2/v2/auth?' + 
    `client_id=${encodeURIComponent(GOOGLE_CLIENT_ID)}` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}` +
    `&response_type=token` +
    `&scope=${encodeURIComponent(GOOGLE_SCOPES)}` +
    `&state=${state}` +
    `&prompt=consent`;
  
  console.log('Full auth URL:', authUrl);
  console.log('Redirecting now...');
  
  // Redirect to Google
  window.location.href = authUrl;
}

/**
 * Handle OAuth redirect callback (check URL hash for token)
 * Call this on page load to check if we're returning from OAuth
 */
export function handleGoogleRedirectCallback() {
  const hash = window.location.hash;
  if (!hash || !sessionStorage.getItem('google_oauth_pending')) {
    return false;
  }
  
  // Parse hash parameters
  const params = new URLSearchParams(hash.substring(1));
  const accessToken = params.get('access_token');
  const state = params.get('state');
  const savedState = sessionStorage.getItem('google_oauth_state');
  const error = params.get('error');
  
  // Clean up
  sessionStorage.removeItem('google_oauth_pending');
  sessionStorage.removeItem('google_oauth_state');
  
  // Clear hash from URL
  if (window.history.replaceState) {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }
  
  if (error) {
    console.error('Google OAuth redirect error:', error);
    return { success: false, error: params.get('error_description') || error };
  }
  
  if (state !== savedState) {
    console.error('Google OAuth state mismatch');
    return { success: false, error: 'Security error: state mismatch' };
  }
  
  if (accessToken) {
    console.log('Google OAuth redirect success');
    googleAccessToken = accessToken;
    try {
      sessionStorage.setItem('google_access_token', accessToken);
    } catch (e) {}
    return { success: true };
  }
  
  return { success: false, error: 'No access token received' };
}

/**
 * Request Google Drive access
 * Uses popup on desktop, with special handling for mobile
 */
export function requestGoogleAccess() {
  return new Promise((resolve, reject) => {
    console.log('=== requestGoogleAccess called ===');
    console.log('googleTokenClient exists:', !!googleTokenClient);
    
    if (!googleTokenClient) {
      reject(new Error('Google Drive not initialized. Please refresh the page and try again.'));
      return;
    }

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    console.log('Is mobile:', isMobile);
    
    // Set a timeout to detect if popup was blocked or callback never fired
    let callbackFired = false;
    let resolved = false;
    
    const timeoutId = setTimeout(() => {
      if (!callbackFired && !resolved) {
        console.error('Google OAuth timeout - callback never fired');
        if (googleAccessToken) {
          resolved = true;
          resolve(googleAccessToken);
        } else {
          reject(new Error('Authentication timed out. Please try again.'));
        }
      }
    }, isMobile ? 30000 : 120000);

    // Set up the callback before requesting the token
    googleTokenClient.callback = (response) => {
      console.log('=== REQUEST Token callback fired ===');
      console.log('Response:', JSON.stringify(response, null, 2));
      console.log('Already resolved:', resolved);
      if (resolved) return;
      callbackFired = true;
      clearTimeout(timeoutId);
      
      if (response.error) {
        console.error('Google OAuth error:', response.error);
        reject(new Error(response.error_description || response.error));
      } else if (response.access_token) {
        console.log('Google OAuth success, got token:', response.access_token.substring(0, 20) + '...');
        googleAccessToken = response.access_token;
        try {
          sessionStorage.setItem('google_access_token', response.access_token);
        } catch (e) {
          console.warn('Could not store token in sessionStorage:', e);
        }
        resolved = true;
        resolve(response.access_token);
      } else {
        reject(new Error('No access token received'));
      }
    };

    // Set up error callback
    googleTokenClient.error_callback = (error) => {
      console.log('=== REQUEST Error callback fired ===');
      console.log('Error:', JSON.stringify(error, null, 2));
      console.log('Already resolved:', resolved);
      console.log('Callback already fired:', callbackFired);
      
      if (resolved) return;
      callbackFired = true;
      clearTimeout(timeoutId);
      
      if (error.type === 'popup_closed') {
        console.log('Popup was closed, checking for token in 1 second...');
        setTimeout(() => {
          if (resolved) return;
          try {
            const storedToken = sessionStorage.getItem('google_access_token');
            console.log('Stored token found:', !!storedToken);
            console.log('googleAccessToken:', !!googleAccessToken);
            if (storedToken || googleAccessToken) {
              console.log('Token found after popup closed - auth succeeded');
              googleAccessToken = storedToken || googleAccessToken;
              resolved = true;
              resolve(googleAccessToken);
              return;
            }
          } catch (e) {
            console.error('Error checking token:', e);
          }
          reject(new Error('Sign-in was cancelled. Please try again.'));
        }, 1000);
        return;
      } else if (error.type === 'popup_failed_to_open') {
        reject(new Error('Could not open sign-in popup. Please allow popups for this site.'));
      } else {
        reject(new Error(error.message || 'Authentication failed. Please try again.'));
      }
    };

    // Also listen for visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !resolved) {
        setTimeout(() => {
          if (resolved) return;
          try {
            const storedToken = sessionStorage.getItem('google_access_token');
            if (storedToken || googleAccessToken) {
              console.log('Token found on visibility change');
              googleAccessToken = storedToken || googleAccessToken;
              callbackFired = true;
              clearTimeout(timeoutId);
              resolved = true;
              document.removeEventListener('visibilitychange', handleVisibilityChange);
              resolve(googleAccessToken);
            }
          } catch (e) {}
        }, 500);
      }
    };
    
    if (isMobile) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    try {
      console.log('Calling requestAccessToken with prompt:', isMobile ? 'select_account' : 'consent');
      googleTokenClient.requestAccessToken({ 
        prompt: isMobile ? 'select_account' : 'consent'
      });
      console.log('requestAccessToken called successfully');
    } catch (err) {
      callbackFired = true;
      clearTimeout(timeoutId);
      if (isMobile) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
      console.error('Error requesting access token:', err);
      reject(err);
    }
  });
}

/**
 * Check if we have Google Drive access
 */
export function hasGoogleAccess() {
  // Try to restore from sessionStorage if not in memory
  if (!googleAccessToken) {
    try {
      const storedToken = sessionStorage.getItem('google_access_token');
      if (storedToken) {
        googleAccessToken = storedToken;
      }
    } catch (e) {
      console.warn('Could not read token from sessionStorage:', e);
    }
  }
  return !!googleAccessToken;
}

/**
 * Backup to Google Drive
 */
export async function backupToGoogleDrive() {
  if (!googleAccessToken) {
    await requestGoogleAccess();
  }

  // Get all data
  const data = await exportAllData();
  const content = JSON.stringify(data, null, 2);
  const blob = new Blob([content], { type: 'application/json' });

  // Check if backup file already exists
  const searchResponse = await fetch(
    `https://www.googleapis.com/drive/v3/files?q=name='${BACKUP_FILENAME}'&spaces=drive`,
    {
      headers: { Authorization: `Bearer ${googleAccessToken}` }
    }
  );

  const searchResult = await searchResponse.json();
  const existingFile = searchResult.files?.[0];

  if (existingFile) {
    // Update existing file
    const formData = new FormData();
    formData.append('file', blob);

    const response = await fetch(
      `https://www.googleapis.com/upload/drive/v3/files/${existingFile.id}?uploadType=media`,
      {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${googleAccessToken}` },
        body: blob
      }
    );

    if (!response.ok) throw new Error('Failed to update backup');
    return { success: true, message: 'Backup updated on Google Drive', fileId: existingFile.id };
  } else {
    // Create new file
    const metadata = {
      name: BACKUP_FILENAME,
      mimeType: 'application/json'
    };

    const formData = new FormData();
    formData.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    formData.append('file', blob);

    const response = await fetch(
      'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart',
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${googleAccessToken}` },
        body: formData
      }
    );

    if (!response.ok) throw new Error('Failed to create backup');
    const result = await response.json();
    return { success: true, message: 'Backup created on Google Drive', fileId: result.id };
  }
}

/**
 * Restore from Google Drive
 */
export async function restoreFromGoogleDrive() {
  if (!googleAccessToken) {
    await requestGoogleAccess();
  }

  // Find backup file
  const searchResponse = await fetch(
    `https://www.googleapis.com/drive/v3/files?q=name='${BACKUP_FILENAME}'&spaces=drive`,
    {
      headers: { Authorization: `Bearer ${googleAccessToken}` }
    }
  );

  const searchResult = await searchResponse.json();
  const backupFile = searchResult.files?.[0];

  if (!backupFile) {
    throw new Error('No backup found on Google Drive');
  }

  // Download file
  const downloadResponse = await fetch(
    `https://www.googleapis.com/drive/v3/files/${backupFile.id}?alt=media`,
    {
      headers: { Authorization: `Bearer ${googleAccessToken}` }
    }
  );

  if (!downloadResponse.ok) throw new Error('Failed to download backup');

  const data = await downloadResponse.json();
  const result = await importAllData(data);

  return { 
    success: true, 
    message: 'Backup restored from Google Drive',
    ...result
  };
}

/**
 * Check if backup exists on Google Drive
 */
export async function checkGoogleDriveBackup() {
  if (!googleAccessToken) {
    return { exists: false, needsAuth: true };
  }

  try {
    const searchResponse = await fetch(
      `https://www.googleapis.com/drive/v3/files?q=name='${BACKUP_FILENAME}'&spaces=drive&fields=files(id,modifiedTime)`,
      {
        headers: { Authorization: `Bearer ${googleAccessToken}` }
      }
    );

    const searchResult = await searchResponse.json();
    const backupFile = searchResult.files?.[0];

    if (backupFile) {
      return { 
        exists: true, 
        lastModified: backupFile.modifiedTime,
        fileId: backupFile.id
      };
    }
    return { exists: false };
  } catch {
    return { exists: false, error: true };
  }
}


// ============== DROPBOX ==============

const DROPBOX_CLIENT_ID = process.env.REACT_APP_DROPBOX_CLIENT_ID;
const DROPBOX_BACKUP_PATH = '/minddeck_backup.json';

let dropboxAccessToken = null;

/**
 * Initialize Dropbox OAuth
 */
export function getDropboxAuthUrl() {
  if (!DROPBOX_CLIENT_ID) {
    throw new Error('Dropbox integration not configured');
  }

  const redirectUri = `${window.location.origin}/backup/dropbox/callback`;
  const state = Math.random().toString(36).substring(7);
  
  // Store state for verification
  sessionStorage.setItem('dropbox_state', state);

  return `https://www.dropbox.com/oauth2/authorize?` +
    `client_id=${DROPBOX_CLIENT_ID}&` +
    `redirect_uri=${encodeURIComponent(redirectUri)}&` +
    `response_type=token&` +
    `state=${state}`;
}

/**
 * Handle Dropbox OAuth callback
 */
export function handleDropboxCallback(hash) {
  const params = new URLSearchParams(hash.replace('#', ''));
  const accessToken = params.get('access_token');
  const state = params.get('state');
  const savedState = sessionStorage.getItem('dropbox_state');

  if (state !== savedState) {
    throw new Error('Invalid state parameter');
  }

  if (accessToken) {
    dropboxAccessToken = accessToken;
    // Store token (will be cleared on page refresh for security)
    sessionStorage.setItem('dropbox_token', accessToken);
    sessionStorage.removeItem('dropbox_state');
    return true;
  }

  throw new Error('No access token received');
}

/**
 * Check if we have Dropbox access
 */
export function hasDropboxAccess() {
  if (!dropboxAccessToken) {
    dropboxAccessToken = sessionStorage.getItem('dropbox_token');
  }
  return !!dropboxAccessToken;
}

/**
 * Get Dropbox access token
 */
export function getDropboxToken() {
  if (!dropboxAccessToken) {
    dropboxAccessToken = sessionStorage.getItem('dropbox_token');
  }
  return dropboxAccessToken;
}

/**
 * Backup to Dropbox
 */
export async function backupToDropbox() {
  const token = getDropboxToken();
  if (!token) {
    throw new Error('Not connected to Dropbox');
  }

  // Get all data
  const data = await exportAllData();
  const content = JSON.stringify(data, null, 2);

  const response = await fetch('https://content.dropboxapi.com/2/files/upload', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/octet-stream',
      'Dropbox-API-Arg': JSON.stringify({
        path: DROPBOX_BACKUP_PATH,
        mode: 'overwrite',
        autorename: false,
        mute: false
      })
    },
    body: content
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error_summary || 'Failed to backup to Dropbox');
  }

  const result = await response.json();
  return { 
    success: true, 
    message: 'Backup saved to Dropbox',
    path: result.path_display
  };
}

/**
 * Restore from Dropbox
 */
export async function restoreFromDropbox() {
  const token = getDropboxToken();
  if (!token) {
    throw new Error('Not connected to Dropbox');
  }

  const response = await fetch('https://content.dropboxapi.com/2/files/download', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Dropbox-API-Arg': JSON.stringify({ path: DROPBOX_BACKUP_PATH })
    }
  });

  if (!response.ok) {
    if (response.status === 409) {
      throw new Error('No backup found on Dropbox');
    }
    const error = await response.json();
    throw new Error(error.error_summary || 'Failed to download from Dropbox');
  }

  const data = await response.json();
  const result = await importAllData(data);

  return { 
    success: true, 
    message: 'Backup restored from Dropbox',
    ...result
  };
}

/**
 * Check if backup exists on Dropbox
 */
export async function checkDropboxBackup() {
  const token = getDropboxToken();
  if (!token) {
    return { exists: false, needsAuth: true };
  }

  try {
    const response = await fetch('https://api.dropboxapi.com/2/files/get_metadata', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ path: DROPBOX_BACKUP_PATH })
    });

    if (response.ok) {
      const metadata = await response.json();
      return { 
        exists: true, 
        lastModified: metadata.server_modified,
        size: metadata.size
      };
    }
    
    return { exists: false };
  } catch {
    return { exists: false, error: true };
  }
}

/**
 * Disconnect from Dropbox
 */
export function disconnectDropbox() {
  dropboxAccessToken = null;
  sessionStorage.removeItem('dropbox_token');
}


// ============== LOCAL FILE EXPORT/IMPORT ==============

import * as XLSX from 'xlsx';

/**
 * Download backup as local file
 */
export async function downloadBackupFile() {
  const data = await exportAllData();
  const content = JSON.stringify(data, null, 2);
  const blob = new Blob([content], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = `minddeck_backup_${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  
  return { success: true, message: 'Backup downloaded' };
}

/**
 * Parse CSV text into array of objects
 */
function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  
  // Parse header
  const headers = lines[0].split(',').map(h => h.trim().replace(/^["']|["']$/g, ''));
  
  // Parse rows
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = [];
    let current = '';
    let inQuotes = false;
    
    for (const char of lines[i]) {
      if (char === '"' && !inQuotes) {
        inQuotes = true;
      } else if (char === '"' && inQuotes) {
        inQuotes = false;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    
    if (values.length === headers.length) {
      const row = {};
      headers.forEach((h, idx) => {
        row[h] = values[idx];
      });
      rows.push(row);
    }
  }
  
  return rows;
}

/**
 * Convert flat card data (from CSV/XLS) to backup format
 * Expected columns: title, body/notes, and any other columns become fields
 */
function convertFlatDataToBackup(rows, workspaceName = 'Imported') {
  const now = new Date().toISOString();
  const generateId = () => Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
  
  const workspaceId = generateId();
  const cards = [];
  
  for (const row of rows) {
    // Get title (required)
    const title = row.title || row.Title || row.name || row.Name || row.card || row.Card;
    if (!title) continue;
    
    // Get body/notes
    const body = row.body || row.Body || row.notes || row.Notes || row.description || row.Description || '';
    
    // All other columns become fields
    const fields = {};
    const reservedKeys = ['title', 'Title', 'name', 'Name', 'card', 'Card', 'body', 'Body', 'notes', 'Notes', 'description', 'Description', 'id', 'ID'];
    
    for (const [key, value] of Object.entries(row)) {
      if (!reservedKeys.includes(key) && value && value.trim()) {
        fields[key] = value.trim();
      }
    }
    
    cards.push({
      id: generateId(),
      workspace_id: workspaceId,
      title: title.trim(),
      body: body.trim(),
      fields,
      is_index: false,
      is_deleted: false,
      created_at: now,
      updated_at: now
    });
  }
  
  return {
    version: 1,
    exported_at: now,
    workspaces: [{
      id: workspaceId,
      name: workspaceName,
      description: `Imported from file on ${new Date().toLocaleDateString()}`,
      is_main: false,
      is_deleted: false,
      created_at: now,
      updated_at: now,
      cards,
      decks: []
    }]
  };
}

/**
 * Import from local file - supports JSON, CSV, XLS, XLSX
 */
export async function importFromFile(file) {
  return new Promise((resolve, reject) => {
    const fileName = file.name.toLowerCase();
    const reader = new FileReader();
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    
    if (fileName.endsWith('.json')) {
      // JSON file - direct backup format
      reader.onload = async (e) => {
        try {
          const data = JSON.parse(e.target.result);
          const result = await importAllData(data);
          resolve({ success: true, ...result });
        } catch (error) {
          reject(new Error('Invalid JSON backup file format'));
        }
      };
      reader.readAsText(file);
      
    } else if (fileName.endsWith('.csv')) {
      // CSV file - flat card data
      reader.onload = async (e) => {
        try {
          const rows = parseCSV(e.target.result);
          if (rows.length === 0) {
            reject(new Error('No valid data found in CSV file'));
            return;
          }
          const backupData = convertFlatDataToBackup(rows, 'CSV Import');
          const result = await importAllData(backupData);
          resolve({ 
            success: true, 
            ...result,
            message: `Imported ${backupData.workspaces[0].cards.length} cards from CSV`
          });
        } catch (error) {
          reject(new Error('Failed to parse CSV file: ' + error.message));
        }
      };
      reader.readAsText(file);
      
    } else if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx')) {
      // Excel file - flat card data
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Get first sheet
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          
          // Convert to JSON
          const rows = XLSX.utils.sheet_to_json(sheet);
          
          if (rows.length === 0) {
            reject(new Error('No valid data found in Excel file'));
            return;
          }
          
          const backupData = convertFlatDataToBackup(rows, 'Excel Import');
          const result = await importAllData(backupData);
          resolve({ 
            success: true, 
            ...result,
            message: `Imported ${backupData.workspaces[0].cards.length} cards from Excel`
          });
        } catch (error) {
          reject(new Error('Failed to parse Excel file: ' + error.message));
        }
      };
      reader.readAsArrayBuffer(file);
      
    } else {
      reject(new Error('Unsupported file type. Please use .json, .csv, .xls, or .xlsx'));
    }
  });
}

/**
 * Import from pasted JSON text
 */
export async function importFromText(text) {
  try {
    const data = JSON.parse(text);
    const result = await importAllData(data);
    return { success: true, ...result };
  } catch (error) {
    throw new Error('Invalid JSON format');
  }
}
